import { useState, useRef, useEffect, forwardRef, useCallback } from 'react';
import { ChevronDown, Plus, Loader2 } from 'lucide-react';

export interface DropdownOption {
  id: string;
  label: string;
  sectionIndex?: number; // Track which section this option belongs to
}

export interface ComboBoxSection {
  title: string;
  fetchOptions: (params: {
    page: number;
    search: string;
  }) => Promise<{
    items: DropdownOption[];
    hasMore: boolean;
  }>;
  emptyMessage?: string; // Message to show when section has no items
}

export interface InfiniteComboBoxProps {
  label?: string;
  error?: string;
  hint?: string;
  value: string;
  onChange: (value: string) => void;
  fetchOptions?: (params: {
    page: number;
    search: string;
  }) => Promise<{
    items: DropdownOption[];
    hasMore: boolean;
  }>;
  sections?: ComboBoxSection[]; // Alternative to fetchOptions for sectioned data
  onCreateNew?: (name: string) => Promise<void>;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  createLabel?: string;
  allowEmpty?: boolean;
  emptyLabel?: string;
}

export const InfiniteComboBox = forwardRef<HTMLDivElement, InfiniteComboBoxProps>(
  (
    {
      label,
      error,
      hint,
      value,
      onChange,
      fetchOptions,
      sections,
      onCreateNew,
      placeholder = 'Select or type to search...',
      disabled = false,
      required = false,
      id,
      createLabel = 'Create new',
      allowEmpty = true,
      emptyLabel = 'None',
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [options, setOptions] = useState<DropdownOption[]>([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [sectionPages, setSectionPages] = useState<number[]>([]); // Track page per section
    const [sectionHasMore, setSectionHasMore] = useState<boolean[]>([]); // Track hasMore per section
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [createError, setCreateError] = useState<string>('');
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedLabel, setSelectedLabel] = useState<string>(''); // Store the label of the selected option

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const listEndRef = useRef<HTMLDivElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    const comboBoxId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const listboxId = `${comboBoxId}-listbox`;
    const createOptionId = `${comboBoxId}-create-option`;

    // Get the display label for the selected value
    // Use cached selectedLabel if available, otherwise try to find in current options
    const selectedOption = options.find((opt) => opt.id === value);
    const displayValue = selectedLabel || selectedOption?.label || (value === '' && allowEmpty ? emptyLabel : '');

    // Update selectedLabel when value changes externally or when it's found in loaded options
    useEffect(() => {
      if (!value || value === '') {
        setSelectedLabel('');
        return;
      }

      // Try to find the label in current options
      const found = options.find(opt => opt.id === value);
      if (found) {
        setSelectedLabel(found.label);
      }
    }, [value, options]);

    // Debounce search input (300ms)
    useEffect(() => {
      const timer = setTimeout(() => {
        setDebouncedSearch(searchQuery);
      }, 300);

      return () => clearTimeout(timer);
    }, [searchQuery]);

    // Fetch options when debounced search changes (preload first page on mount)
    useEffect(() => {
      const loadOptions = async () => {
        setIsLoading(true);
        setOptions([]);
        setPage(1);
        setHasMore(true);

        try {
          if (sections) {
            // Initialize section tracking
            setSectionPages(sections.map(() => 1));
            setSectionHasMore(sections.map(() => true));

            // Load first page of all sections
            const allOptions: DropdownOption[] = [];
            const hasMoreFlags: boolean[] = [];

            for (let i = 0; i < sections.length; i++) {
              const result = await sections[i].fetchOptions({ page: 1, search: debouncedSearch });
              const sectionOptions = result.items.map(item => ({
                ...item,
                sectionIndex: i,
              }));
              allOptions.push(...sectionOptions);
              hasMoreFlags[i] = result.hasMore;
            }

            setOptions(allOptions);
            setSectionHasMore(hasMoreFlags);
            // Global hasMore is true if any section has more
            setHasMore(hasMoreFlags.some(h => h));
          } else if (fetchOptions) {
            // Single source mode (backward compatible)
            const result = await fetchOptions({ page: 1, search: debouncedSearch });
            setOptions(result.items);
            setHasMore(result.hasMore);
          }
        } catch (error) {
          console.error('Failed to fetch options:', error);
          setOptions([]);
          setHasMore(false);
        } finally {
          setIsLoading(false);
        }
      };

      loadOptions();
    }, [debouncedSearch, fetchOptions, sections]);

    // Load more options when scrolling near bottom
    const loadMoreOptions = useCallback(async () => {
      if (!hasMore || isLoadingMore || isLoading) return;

      setIsLoadingMore(true);
      try {
        if (sections) {
          // Find the first section with more pages
          const sectionWithMore = sectionHasMore.findIndex(h => h);
          if (sectionWithMore === -1) {
            setHasMore(false);
            return;
          }

          const nextPage = sectionPages[sectionWithMore] + 1;
          const result = await sections[sectionWithMore].fetchOptions({
            page: nextPage,
            search: debouncedSearch,
          });

          const newOptions = result.items.map(item => ({
            ...item,
            sectionIndex: sectionWithMore,
          }));

          setOptions(prev => [...prev, ...newOptions]);

          // Update this section's page and hasMore
          setSectionPages(prev => {
            const updated = [...prev];
            updated[sectionWithMore] = nextPage;
            return updated;
          });

          setSectionHasMore(prev => {
            const updated = [...prev];
            updated[sectionWithMore] = result.hasMore;
            // Global hasMore is true if any section has more
            setHasMore(updated.some(h => h));
            return updated;
          });
        } else if (fetchOptions) {
          // Single source mode (backward compatible)
          const nextPage = page + 1;
          const result = await fetchOptions({ page: nextPage, search: debouncedSearch });
          setOptions((prev) => [...prev, ...result.items]);
          setPage(nextPage);
          setHasMore(result.hasMore);
        }
      } catch (error) {
        console.error('Failed to fetch more options:', error);
      } finally {
        setIsLoadingMore(false);
      }
    }, [hasMore, isLoadingMore, isLoading, page, sectionPages, sectionHasMore, debouncedSearch, fetchOptions, sections]);

    // Set up IntersectionObserver for infinite scroll
    useEffect(() => {
      if (!isOpen || !hasMore) return;

      const observer = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (firstEntry.isIntersecting && hasMore && !isLoadingMore && !isLoading) {
            loadMoreOptions();
          }
        },
        { threshold: 0.7 } // Trigger when 70% visible
      );

      if (listEndRef.current) {
        observer.observe(listEndRef.current);
      }

      observerRef.current = observer;

      return () => {
        if (observerRef.current) {
          observerRef.current.disconnect();
        }
      };
    }, [isOpen, hasMore, isLoadingMore, isLoading, loadMoreOptions]);

    // Check if search query matches exactly with an existing option
    const exactMatch = options.some(
      (opt) => opt.label.toLowerCase() === searchQuery.toLowerCase()
    );

    // Show "Create new" option if we have onCreateNew, query is not empty, and no exact match
    const showCreateOption = onCreateNew && searchQuery.trim() && !exactMatch;

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
          setSearchQuery('');
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }
    }, [isOpen]);

    const handleInputClick = () => {
      if (!disabled) {
        setIsOpen(true);
      }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
      setIsOpen(true);
      setActiveIndex(-1);
      setCreateError('');
    };

    const handleSelectOption = (optionId: string) => {
      // Store the label of the selected option for display
      const selected = options.find(opt => opt.id === optionId);
      if (selected) {
        setSelectedLabel(selected.label);
      }
      onChange(optionId);
      setIsOpen(false);
      setSearchQuery('');
    };

    const handleCreateNew = async () => {
      if (!onCreateNew || !searchQuery.trim()) return;

      setIsCreating(true);
      setCreateError('');
      try {
        await onCreateNew(searchQuery.trim());
        setSearchQuery('');
        setIsOpen(false);
        inputRef.current?.focus();
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to create new option';
        setCreateError(errorMessage);
        console.error('Failed to create new option:', error);
      } finally {
        setIsCreating(false);
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const totalOptions = options.length + (showCreateOption ? 1 : 0);

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!isOpen) {
            setIsOpen(true);
          } else {
            setActiveIndex((prev) => (prev < totalOptions - 1 ? prev + 1 : prev));
          }
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (isOpen) {
            setActiveIndex((prev) => (prev > 0 ? prev - 1 : -1));
          }
          break;

        case 'Home':
          if (isOpen) {
            e.preventDefault();
            setActiveIndex(0);
          }
          break;

        case 'End':
          if (isOpen) {
            e.preventDefault();
            setActiveIndex(totalOptions - 1);
          }
          break;

        case 'Enter':
          e.preventDefault();
          if (isOpen) {
            if (activeIndex >= 0 && activeIndex < options.length) {
              handleSelectOption(options[activeIndex].id);
            } else if (showCreateOption && activeIndex === options.length) {
              handleCreateNew();
            } else if (showCreateOption && activeIndex === -1) {
              handleCreateNew();
            }
          }
          break;

        case 'Escape':
          setIsOpen(false);
          setSearchQuery('');
          setActiveIndex(-1);
          setCreateError('');
          break;

        case 'Tab':
          setIsOpen(false);
          setSearchQuery('');
          setActiveIndex(-1);
          break;
      }
    };

    return (
      <div ref={ref} className="w-full">
        {label && (
          <label
            htmlFor={comboBoxId}
            className="mb-1.5 block text-sm font-medium text-foreground"
          >
            {label}
            {required && <span className="ml-1 text-error">*</span>}
          </label>
        )}
        <div ref={containerRef} className="relative">
          <div className="relative">
            <input
              ref={inputRef}
              id={comboBoxId}
              type="text"
              role="combobox"
              aria-expanded={isOpen}
              aria-controls={listboxId}
              aria-activedescendant={
                activeIndex >= 0 && activeIndex < options.length
                  ? `${comboBoxId}-option-${activeIndex}`
                  : activeIndex === options.length && showCreateOption
                  ? createOptionId
                  : undefined
              }
              aria-autocomplete="list"
              aria-required={required}
              value={isOpen ? searchQuery : displayValue}
              onChange={handleInputChange}
              onClick={handleInputClick}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              required={required}
              className={`
                block w-full rounded-md border bg-background-subtle text-foreground
                focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary
                disabled:cursor-not-allowed disabled:opacity-50
                h-10 pl-3 pr-10 text-sm
                ${error || createError ? 'border-error' : 'border-border'}
              `}
            />
            <button
              type="button"
              onClick={handleInputClick}
              disabled={disabled}
              className="absolute inset-y-0 right-0 flex items-center pr-3 text-foreground-subtle"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
          </div>

          {isOpen && (
            <div
              id={listboxId}
              role="listbox"
              className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-background-elevated shadow-lg"
            >
              {isLoading && (
                <div className="flex items-center justify-center px-3 py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-foreground-muted" />
                </div>
              )}

              {!isLoading && options.length === 0 && !showCreateOption && (
                <div className="px-3 py-2 text-sm text-foreground-muted">
                  No options found
                </div>
              )}

              {!isLoading && sections ? (
                // Render sectioned options with headers
                <>
                  {sections.map((section, sectionIdx) => {
                    const sectionOptions = options.filter(opt => opt.sectionIndex === sectionIdx);

                    return (
                      <div key={`section-${sectionIdx}`}>
                        {/* Section header */}
                        <div className="sticky top-0 z-10 border-b border-border bg-background-elevated px-3 py-1.5 text-xs font-semibold text-foreground-muted">
                          {section.title}
                        </div>
                        {/* Section options or empty message */}
                        {sectionOptions.length === 0 ? (
                          section.emptyMessage && (
                            <div className="px-3 py-2 text-sm text-foreground-muted italic">
                              {section.emptyMessage}
                            </div>
                          )
                        ) : (
                          sectionOptions.map((option) => {
                            const globalIndex = options.indexOf(option);
                            return (
                              <button
                                key={option.id}
                                id={`${comboBoxId}-option-${globalIndex}`}
                                role="option"
                                type="button"
                                aria-selected={value === option.id}
                                onClick={() => handleSelectOption(option.id)}
                                className={`
                                  w-full px-3 py-2 text-left text-sm
                                  hover:bg-background-subtle
                                  ${value === option.id ? 'bg-background-subtle font-medium' : ''}
                                  ${activeIndex === globalIndex ? 'bg-background-subtle' : ''}
                                `}
                              >
                                {option.label}
                              </button>
                            );
                          })
                        )}
                      </div>
                    );
                  })}
                </>
              ) : (
                // Render non-sectioned options (backward compatible)
                !isLoading &&
                options.map((option, index) => (
                  <button
                    key={option.id}
                    id={`${comboBoxId}-option-${index}`}
                    role="option"
                    type="button"
                    aria-selected={value === option.id}
                    onClick={() => handleSelectOption(option.id)}
                    className={`
                      w-full px-3 py-2 text-left text-sm
                      hover:bg-background-subtle
                      ${value === option.id ? 'bg-background-subtle font-medium' : ''}
                      ${activeIndex === index ? 'bg-background-subtle' : ''}
                    `}
                  >
                    {option.label}
                  </button>
                ))
              )}

              {!isLoading && hasMore && (
                <div ref={listEndRef} className="flex items-center justify-center px-3 py-2">
                  {isLoadingMore && <Loader2 className="h-4 w-4 animate-spin text-foreground-muted" />}
                </div>
              )}

              {!isLoading && showCreateOption && (
                <button
                  id={createOptionId}
                  role="option"
                  type="button"
                  aria-selected={false}
                  onClick={handleCreateNew}
                  disabled={isCreating}
                  className={`
                    flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm text-primary hover:bg-background-subtle disabled:cursor-not-allowed disabled:opacity-50
                    ${activeIndex === options.length ? 'bg-background-subtle' : ''}
                  `}
                >
                  <Plus className="h-4 w-4" />
                  <span>
                    {isCreating ? 'Creating...' : `${createLabel}: "${searchQuery}"`}
                  </span>
                </button>
              )}
            </div>
          )}
        </div>

        {(error || createError) && (
          <p className="mt-1.5 text-sm text-error">{error || createError}</p>
        )}
        {hint && !error && !createError && (
          <p className="mt-1.5 text-sm text-foreground-muted">{hint}</p>
        )}
      </div>
    );
  }
);

InfiniteComboBox.displayName = 'InfiniteComboBox';
